import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, StickyNote, ExternalLink, X } from "lucide-react";
import { formatDateIt } from "@/lib/format";
import { toast } from "sonner";

function highlight(text, q) {
  if (!q) return text;
  const parts = text.split(new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) => {
    const stableKey = `${i}-${p}`;
    return p.toLowerCase() === q.toLowerCase() ? (
      <mark key={stableKey} className="bg-[#F56A1F]/25 text-[#F56A1F] rounded px-0.5">{p}</mark>
    ) : (
      <span key={stableKey}>{p}</span>
    );
  });
}

export default function RicercaNote() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const doSearch = async (e) => {
    if (e) e.preventDefault();
    if (q.trim().length < 2) {
      toast.error("Inserisci almeno 2 caratteri");
      return;
    }
    setLoading(true);
    try {
      const r = await api.get(`/notes/search`, { params: { q: q.trim() } });
      setResults(r.data);
    } catch {
      toast.error("Errore nella ricerca");
    } finally {
      setLoading(false);
    }
  };

  const clear = () => { setQ(""); setResults(null); };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.16em] text-[#F56A1F] font-semibold mb-2">
          Archivio Storico
        </div>
        <h1 className="text-4xl font-semibold tracking-tight text-stone-900">Ricerca nelle Note</h1>
        <p className="text-stone-500 mt-1 max-w-2xl">
          Cerca dentro il testo di tutte le note giornaliere per ritrovare velocemente cosa hai detto
          a un cliente mesi (o anni) fa.
        </p>
      </div>

      <Card className="border-stone-200 shadow-sm rounded-2xl p-5 mb-6">
        <form onSubmit={doSearch} className="flex gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
            <Input
              autoFocus
              data-testid="ricerca-note-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder='Es. "preventivo", "lampioni LED", "cantiere Modena"…'
              className="pl-9 pr-9 h-11 bg-white"
            />
            {q && (
              <button
                type="button"
                onClick={clear}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-stone-400 hover:text-stone-700 hover:bg-stone-100">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button
            type="submit"
            data-testid="ricerca-note-submit"
            disabled={loading}
            className="bg-[#F56A1F] hover:bg-[#E05206] text-white gap-2 h-11 px-6">
            <Search className="w-4 h-4" />
            {loading ? "Ricerca…" : "Cerca"}
          </Button>
        </form>
      </Card>

      {results && (
        <div className="mb-4 flex items-center justify-between">
          <div className="text-sm text-stone-600">
            <span className="font-semibold text-stone-900">{results.count}</span>{" "}
            {results.count === 1 ? "risultato" : "risultati"} per{" "}
            <span className="text-[#F56A1F] font-medium">"{results.query}"</span>
          </div>
        </div>
      )}

      {results && results.hits.length === 0 && (
        <Card className="border-dashed border-stone-200 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-stone-100 flex items-center justify-center mb-3">
            <StickyNote className="w-6 h-6 text-stone-400" />
          </div>
          <div className="text-stone-500">Nessuna nota trovata contenente "{results.query}".</div>
        </Card>
      )}

      {results && results.hits.length > 0 && (
        <div className="space-y-3">
          {results.hits.map((h) => (
            <Card
              key={h.note_id}
              data-testid={`ricerca-hit-${h.note_id}`}
              onClick={() => navigate(`/clienti/${h.client_id}`)}
              className="border-stone-200 rounded-xl p-4 cursor-pointer hover:border-stone-300 hover:shadow-sm transition-all"
            >
              <div className="flex items-start gap-4">
                <div className="w-16 shrink-0 text-center">
                  <div className="text-xs uppercase tracking-widest text-[#F56A1F] font-semibold">
                    {new Date(h.data).toLocaleDateString("it-IT", { month: "short" })}
                  </div>
                  <div className="text-2xl font-semibold text-stone-900 leading-none">
                    {new Date(h.data).getDate()}
                  </div>
                  <div className="text-xs text-stone-400 mt-0.5">{new Date(h.data).getFullYear()}</div>
                </div>
                <div className="flex-1 border-l border-stone-200 pl-4 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="font-medium text-stone-900 truncate">{h.client_ragione_sociale}</div>
                    {h.client_citta && (
                      <div className="text-xs text-stone-400">· {h.client_citta}{h.client_provincia ? ` (${h.client_provincia})` : ""}</div>
                    )}
                    <ExternalLink className="w-3.5 h-3.5 text-stone-300 ml-auto" />
                  </div>
                  <div className="text-sm text-stone-700 leading-relaxed">{highlight(h.snippet, results.query)}</div>
                  <div className="text-[11px] text-stone-400 mt-1.5">{formatDateIt(h.data)}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {!results && !loading && (
        <Card className="border-dashed border-stone-200 rounded-2xl p-12 text-center">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#F56A1F]/10 flex items-center justify-center mb-3">
            <Search className="w-6 h-6 text-[#F56A1F]" />
          </div>
          <div className="text-stone-700 font-medium mb-1">Cerca in oltre 700 note storiche</div>
          <div className="text-stone-500 text-sm max-w-md mx-auto">
            Prova a cercare un nome, un progetto, una città o una parola chiave.
            Le corrispondenze verranno evidenziate nel testo.
          </div>
        </Card>
      )}
    </div>
  );
}
